import FilterListIcon from "@mui/icons-material/FilterList"
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  styled,
} from "@mui/material"
import { useState } from "react"
import { extraColors } from "../../utils/theme"
import {
  type StatusChipColor,
  operationalStatusChipColors,
  operationalStatusLabels,
  sensorTypeLabels,
} from "../Cop/copUtils"
import { Tag } from "../Shared/GenericTag"
import {
  ALL_OPERATIONAL_STATUSES,
  ALL_SENSOR_TYPES,
  useBioMeshUIState,
} from "./BioMeshUIStateContext"

const ToggleButton = styled(IconButton, {
  shouldForwardProp: prop => prop !== "isOpen",
})<{ isOpen: boolean }>(({ isOpen }) => ({
  position: "absolute",
  top: 16,
  left: 16,
  backgroundColor: isOpen ? extraColors.status.navyMuted : extraColors.dark,
  border: `1px solid ${extraColors.translucentWhite}`,
  color: extraColors.white,
  "&:hover": { backgroundColor: extraColors.dark5 },
  width: 32,
  height: 32,
}))

const Panel = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 56,
  left: 16,
  display: "grid",
  gap: theme.spacing(2),
  backgroundColor: extraColors.dark,
  borderRadius: 2,
  border: `1px solid ${extraColors.translucentWhite}`,
  padding: "6px 12px",
  maxHeight: "calc(100% - 32px)",
  overflow: "auto",
}))

const Section = styled("div")({
  display: "grid",
  gap: 2,
})

const SectionHeading = styled(Typography)({
  color: extraColors.hint,
})

const FilterRow = styled(FormControlLabel)({
  margin: 0,
  gap: 4,
})

const FilterCheckbox = styled(Checkbox)({
  padding: 4,
})

const StatusTag = styled(Tag, {
  shouldForwardProp: prop => prop !== "chipColor",
})<{ chipColor: StatusChipColor }>(({ chipColor }) => ({
  backgroundColor: chipColor.bg,
  color: chipColor.fg,
  borderColor: chipColor.border,
}))

export const BioMeshFilterPanel = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { filters, toggleSensorType, toggleOperationalStatus } =
    useBioMeshUIState()

  return (
    <>
      <ToggleButton
        onClick={() => setIsOpen(prev => !prev)}
        aria-label="Toggle filters"
        isOpen={isOpen}
      >
        <FilterListIcon fontSize="small" />
      </ToggleButton>

      {isOpen && (
        <Panel>
          <Section>
            <SectionHeading variant="body2">Sensor Type</SectionHeading>
            {ALL_SENSOR_TYPES.map(st => (
              <FilterRow
                key={st}
                control={
                  <FilterCheckbox
                    checked={filters.sensorTypes.has(st)}
                    onChange={() => toggleSensorType(st)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {sensorTypeLabels[st]}
                  </Typography>
                }
              />
            ))}
          </Section>

          <Section>
            <SectionHeading variant="body2">Operational Status</SectionHeading>
            {ALL_OPERATIONAL_STATUSES.map(os => (
              <FilterRow
                key={os}
                control={
                  <FilterCheckbox
                    checked={filters.operationalStatuses.has(os)}
                    onChange={() => toggleOperationalStatus(os)}
                    size="small"
                  />
                }
                label={
                  <StatusTag chipColor={operationalStatusChipColors[os]}>
                    <Typography variant="small1Bold" color="inherit">
                      {operationalStatusLabels[os]}
                    </Typography>
                  </StatusTag>
                }
              />
            ))}
          </Section>
        </Panel>
      )}
    </>
  )
}
